import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

export function money(cents) {
  const n = (Number(cents) || 0) / 100;
  return n.toLocaleString("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
}
export function dt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); } catch { return String(iso); }
}
export function day(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-DE", { dateStyle: "medium" }); } catch { return String(iso); }
}

export function useFetch(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get(path); setData(res.data); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [path]);
  useEffect(() => { reload(); }, [reload]);
  return { data, loading, reload, setData };
}

const TONE = { default: "text-foreground", good: "text-emerald-600", warn: "text-amber-600", bad: "text-rose-600" };

export function Stat({ label, value, hint, tone = "default", testId }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={testId}>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`mt-1 font-display text-2xl font-semibold ${TONE[tone] || TONE.default}`}>{value}</p>
      {hint && <p className="mt-0.5 truncate text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function Section({ title, action, children, testId }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-5" data-testid={testId}>
      {(title || action) && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          {title ? <h2 className="text-sm font-semibold text-foreground">{title}</h2> : <span />}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function Loading() {
  return <div className="space-y-3"><Skeleton className="h-24 rounded-2xl" /><Skeleton className="h-48 rounded-2xl" /></div>;
}
export function Empty({ text }) {
  return <p className="rounded-2xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">{text}</p>;
}

const PILL = { default: "bg-secondary text-muted-foreground", good: "bg-emerald-100 text-emerald-700", warn: "bg-amber-100 text-amber-700", bad: "bg-rose-100 text-rose-700", info: "bg-sky-100 text-sky-700" };
export function Pill({ children, tone = "default" }) {
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${PILL[tone] || PILL.default}`}>{children}</span>;
}

export function RawJson({ data, label = "Rohdaten anzeigen" }) {
  const [open, setOpen] = useState(false);
  if (data == null) return null;
  return (
    <div className="mt-2">
      <button onClick={() => setOpen((o) => !o)} className="text-xs text-muted-foreground underline" data-testid="admin-rawjson-toggle">{open ? "Rohdaten ausblenden" : label}</button>
      {open && <pre className="mt-1 max-h-80 overflow-auto rounded-lg bg-secondary/60 p-2 text-[11px] leading-relaxed text-foreground">{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}
